import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    Grid,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Collapse,
    InputAdornment,
    TextField,
    Paper,
    Divider,
} from '@mui/material';
import moment from 'moment';
import Routes from '../../../routes';
import UserInfo from '../../../UserInfo';
import {
    LoadingBar,
    LoadingButton,
    PrimaryButton,
    BreadCrumbs,
    PageTitleWithToolTip,
    Select,
    DateInput,
    FilterList,
    CircularLoading,
    ReweighModal,
} from '../../../components/Common';
import CylinderSelect from '../CylinderSelect';
import CylinderDetails from '../CylinderSelect/CylinderDetails';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import NoTableDataIcon from '../../../Icons/empty_table_placeholder.svg';
import {
    locationLabelStyle,
    searchBarStyle,
    selectCylinderStyle,
    filterOptionColumns,
} from './utils';
import { trakrefBlue, inputGrey } from '../../../color_utils';
import { fetchFilterValues, isDesktop, isMobile } from '../../../utils';
import { renderWeight } from '../../../Utils/weight_utils';
import '../../../components/Common/Common.css';
import Enums from '../../../enums'

const { LocationType } = Enums;

const MoveCylinders = ({
    history,
    sendToRecycling = false,
}) => {
    const today = new Date();
    const [state, setAllState] = useState({
        dropdownOptions: [],
        dropDownLocations: [],
        savedLocations: [],
        recycleLocations: [],
        dateMoved: '',
        movedTo: '',
        loading: true,
        expanded: '',
        searchTerm: '',
        locationSearchTerm: '',
        cylinderIdsToMove: [],
        cylindersToMove: [],
        selectedCylinder: '',
        selectedCylinderLocationId: '',
        materialTypes: [],
        renderSuccess: true,
        saving: false,
        reweigh: {
            open: false,
            reweighDate: '',
            newGasWeightLbs: '',
            reasonId: '',
            notes: '',
            saving: false,
        },
        errors: {
            dateMoved: false,
            dateMovedErrorText: "",
            movedTo: false,
        },
    });
    const [filterState, setNewFilterState] = useState({
        tableURL: "MoveCylinders/",
        filterListOptions: filterOptionColumns,
        filterValues: [],
        filters: sessionStorage.getItem("MoveCylinders/" + "filters" + UserInfo.instanceId) == null ? [] : JSON.parse(sessionStorage.getItem("MoveCylinders/" + "filters" + UserInfo.instanceId)),
        appliedFilters: [],
        loading: false,
        selectedFilter: null,
        clearFilters: sessionStorage.getItem("MoveCylinders/" + "filters" + UserInfo.instanceId) == null ? false : true,
    });

    const reweighModalButtonDisabled = (
        !state.reweigh.reweighDate
        || !state.reweigh.newGasWeightLbs
        || !state.reweigh.reasonId
        || state.reweigh.saving
    );

    const handleXButton = () => history.goBack();

    const setState = (new_state_obj) => (
        setAllState((prevState) => (
            { ...prevState, ...new_state_obj }
        ))
    );

    const setFilterState = (new_filter_state_obj) => (
        setNewFilterState((prevFilterState) => (
            { ...prevFilterState, ...new_filter_state_obj }
        ))
    );

    const setReweighState = (new_reweigh_obj) => (
        setAllState((prevState) => (
            { ...prevState, reweigh: { ...prevState.reweigh, ...new_reweigh_obj } }
        ))
    );

    const setErrorState = (new_error_obj) => (
        setAllState((prevState) => (
            { ...prevState, errors: { ...new_error_obj } }
        ))
    );

    const resetReweighState = () => (
        setAllState((prevState) => (
            {
                ...prevState,
                reweigh: {
                    open: false,
                    reweighDate: '',
                    newGasWeightLbs: '',
                    reasonId: '',
                    notes: '',
                    saving: false,
                }
            }
        ))
    );

    const checkForRequiredFields = () => {
        const { dateMoved, movedTo } = state;
        return dateMoved && movedTo;
    };

    const determinePageTitle = () => {
        let pageTitle = "Move Cylinders";

        if (sendToRecycling) {
            pageTitle = "Send Cylinders to Recycling";
        }
        return pageTitle;
    };

    const determineTootTipText = () => {
        let text = `Cylinders are often moved from one location
            to another. Trakref uses the move function which enables
            users to transfer cylinders between different locations
            such as warehouses, tech trucks, and service locations.
        `;

        if (sendToRecycling) {
            text = `When a cylinder is no longer usable due to being
                empty or containing contaminated refrigerant, it can be sent
                for recycling. Trakref offers a 'send to recycling' feature
                which allows for easy transfer of these cylinders to a destructor
                or metal trash bin, depending on the type of cylinder.
            `;
        }

        return text;
    };

    const determineSuccessText = (locationName) => (
        `The cylinders
            ${sendToRecycling
                ? 'have been successfully sent to recycling. '
                : ` were successfully moved to ${locationName}. `
            }
        `
    );

    const validateDateMoved = () => moment(state.dateMoved).isSameOrBefore(today, 'day');

    const validateCylinderSelection = () => state.cylindersToMove && state.cylindersToMove.length > 0;

    const setErrors = () => {
        const { movedTo, dateMoved } = state;
        const dateMovedIsTodayOrBeforeToday = moment(dateMoved).isSameOrBefore(today, 'day');
        const dateMovedValidFormat = moment(dateMoved, 'MM-DD-YYYY', true).isValid();
        let movedToError = false;
        let dateMovedError = false;
        let dateMovedErrorText = "";

        if (!movedTo) movedToError = true;
        if (!dateMoved) dateMovedError = true;

        if (dateMoved && !dateMovedIsTodayOrBeforeToday) {
            dateMovedError = true;
            dateMovedErrorText = "Cannot be a future date";
        }

        if (dateMoved && !dateMovedValidFormat) {
            dateMovedError = true;
            dateMovedErrorText = "Date format is invalid";
        }

        return setErrorState({ movedTo: movedToError, dateMoved: dateMovedError, dateMovedErrorText });
    };

    const handleOpen = (panelIdToOpen, isExpanded) => {
        const open = isExpanded ? panelIdToOpen : false;
        if (!open) {
            closeCylinderDetails();
        } 
        return setState({ expanded: open });
    };

    const getRawLocationData = async () => {
        const raw_location_data = await fetch(
            Routes.Location.GetLocationsForInstance(),
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    filters: filterState.filters
                })
            }
        );

        const savedLocations = await raw_location_data.json();
        return savedLocations;
    };

    const getRecycleLocations = (savedLocations) => (
        savedLocations.filter(l => l.isDestructor && l.isOffsite)
    );

    const fetchData = async () => {
        let savedLocations = await getRawLocationData();
        await Promise.all([
            fetch(Routes.CodesCache.EnabledMaterialTypes()),
            fetchFilterValues(Routes.Asset.FetchMoveCylinderFilterValues()),
        ]).then(async ([rawMaterialTypes, filterValues]) => {
            const materialTypes = await rawMaterialTypes.json();
            let dropDownLocations = savedLocations.filter(l => !l.isDestructor && l.cylinders.length > 0);
            let dropdownOptions = [];
            const recycleLocations = getRecycleLocations(savedLocations);

            if (sendToRecycling) {
                savedLocations = savedLocations.filter(l => l.cylinders.length > 0);
                dropdownOptions = recycleLocations.map(l => ({ id: l.id, name: l.name }));
            } else {
                dropdownOptions = savedLocations
                                    .filter(l => !l.isDestructor && l.locationTypeId !== LocationType.WholesalerSupplier)
                                    .map(l => ({ id: l.id, name: l.name }));
                savedLocations = savedLocations.filter(l => !l.isDestructor && l.cylinders.length > 0);
            }

            setFilterState({
                filterValues,
            });
    
            return setState({
                dropdownOptions,
                dropDownLocations,
                savedLocations,
                recycleLocations,
                materialTypes,
                loading: false
            });

        })
        .catch(error => console.log('Error', error));
    };

    const moveCylinders = async () => {
        const requiredFieldsAreValid = checkForRequiredFields();
        const dateMovedIsValid = validateDateMoved();
        const atLeastOneCylinderSelected = validateCylinderSelection();

        if (!requiredFieldsAreValid || !dateMovedIsValid) return setErrors();

        if (!atLeastOneCylinderSelected) {
            return toast.error('Please select at least one cylinder', { duration: 5000 });
        }

        const submitData = async () => {
            const cylindersFailedToMove = [];
            setErrorState({ movedTo: false, dateMoved: false });
            setState({ saving: true });
            const { movedTo, cylindersToMove, dateMoved, dropdownOptions, recycleLocations } = state;

            const finalCylinderList = cylindersToMove.filter((cylinder) => {
                const { acquisitionDate, installDate } = cylinder;
                if (
                    (installDate && moment(dateMoved).isBefore(installDate, 'day'))
                    || (acquisitionDate && moment(dateMoved).isBefore(acquisitionDate, 'day'))
                ) {
                    cylindersFailedToMove.push(cylinder);
                    return false;
                }
                return true;
            });

            // console.log('cylindersFAILED', cylindersFailedToMove);

            const response = await fetch(Routes.Asset.MoveCylinders(
                movedTo,
                moment(dateMoved).format('YYYY-MM-DD HH:mm:ss'),
                sendToRecycling
            ), {
                method : "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body : JSON.stringify(finalCylinderList.map(c => c.id))
            });
            const assetsSuccessfullyMoved = await response.json();
            console.log('assets assetsSuccessfullyMoved', assetsSuccessfullyMoved);

            if(response.status === 200 && cylindersFailedToMove.length <= 0) {
                const locationMovedTo = sendToRecycling ? recycleLocations : dropdownOptions;
                const location = locationMovedTo.find(l => l.id === movedTo);
                toast.success(determineSuccessText(location.name), { duration: 6000 });
                setState({ saving: false, cylindersToMove: [], cylinderIdsToMove: [] });
                setTimeout(() => history.push('/Inventory'), 5000);
                return fetchData();
            } else if (response.status === 200 && cylindersFailedToMove.length > 0) {
                setState({ saving: false });
                const text = `The following cylinders could not be moved
                    because the move date was before the install date or acquisition date:
                    ${cylindersFailedToMove.map(c => c.name).join(', ')}
                `
                toast.error(text, { duration: 6000 });
                return fetchData();
            } else {
                setState({ saving: false });
                return toast.error("Something went wrong. Cylinders could not be moved.", { duration: 6000 });
            }
        };

        await submitData();
    };

    const setChecked = (isChecked, cylinderId, cylinder) => {
        const { cylinderIdsToMove, cylindersToMove } = state;
        console.log('isChecked', isChecked);
        console.log('checked cylinderId', cylinderId);

        // if already checked, remove cylinder ID from the list
        if (!isChecked) {
            const filteredCylinderIdList = cylinderIdsToMove.filter(id => id !== cylinderId);
            const filteredCylinderList = cylindersToMove.filter(cyl => cyl.id !== cylinderId);
            return setState({
                cylinderIdsToMove: filteredCylinderIdList,
                cylindersToMove: filteredCylinderList
            });
        }

        return setState({
            cylinderIdsToMove: [...cylinderIdsToMove, cylinderId],
            cylindersToMove: [...cylindersToMove, cylinder],
        });
    };

    const setCylinderDetails = (locationId, cylinderId) => {
        console.log('locationId', locationId);
        console.log('cylinderId', cylinderId);
        const { savedLocations } = state;
        const location = savedLocations.find(location => location.id === locationId);
        const { cylinders } = location;
        const selectedCylinder = cylinders.find(cyl => cyl.id === cylinderId);;

        return setState({ selectedCylinder, selectedCylinderLocationId: locationId });
    };

    const closeCylinderDetails = () => setState({ selectedCylinder: '', selectedCylinderLocationId: '' });

    const handleInput = (key, value) => {
        const { errors } = state;

        if (errors[key]) setErrorState({ [key]: false });
        return setState({ [key]: value });
    };

    const searchCylinders = (cylinders, searchTerm) => (
        cylinders.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleSearch = (searchTerm, locationId) => {
        setState({ searchTerm });
        const { savedLocations } = state;
        let dropDownLocations = [...savedLocations];
        const locationIndex = savedLocations.findIndex(l => l.id === locationId);
        const searchCylinderLocation = savedLocations.find(l => l.id === locationId);
        let filteredDropDownLocation = {...searchCylinderLocation};
        const { cylinders } = searchCylinderLocation;
        const searchResults = searchCylinders(cylinders, searchTerm);
        console.log('searchResults', searchResults);
        filteredDropDownLocation.cylinders = searchResults;
        dropDownLocations[locationIndex] = filteredDropDownLocation;

        return setState({ dropDownLocations });
    };
    
    const handleLocationSearch = (locationSearchTerm) => {
        setState({ locationSearchTerm });
        const { savedLocations } = state;
        let dropDownLocations = [...savedLocations]
            .filter(l => l.name.toLowerCase().includes(locationSearchTerm.toLowerCase()));
        
        return setState({ dropDownLocations });
    };

    const handleReweighSubmit = async () => {
        const { selectedCylinder, reweigh } = state;
        const reweighData = {
            oldGasWeightLbs: selectedCylinder.currentGasWeightLbs,
            newGasWeightLbs: reweigh.newGasWeightLbs,
            cylinderId: selectedCylinder.id,
            notes: reweigh.notes,
            cylinderReweighReasonId: reweigh.reasonId,
            reweighDate: moment(reweigh.reweighDate).format('YYYY-MM-DD HH:mm:ss'),
        };
        // console.log('reweighData', reweighData);

        const submitData = async () => {
            setReweighState({ saving: true });
            const response = await fetch(Routes.Asset.CreateReweigh(), {
                method : "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body : JSON.stringify(reweighData)
            });
            const data = await response.json();
            console.log('reweigh data RESPONSE', data);

            if(response.status === 200) {
                setReweighState({ saving: false, open: false });
                toast.success("Cylinders has been successfully reweighed.", { duration: 6000 });
                await fetchData();
                return resetReweighState();
            } else {
                resetReweighState();
                return toast.error("Something went wrong. Cylinder could not be reweighed.", { duration: 6000 });
            }
        };

        await submitData();
    };

    const formatDate = (field) => {
        if (state.selectedCylinder[field]) {
            return moment(state.selectedCylinder[field]).format("L");
        }
        return '-';
    };
    
    const getStyle = (property) => {
        switch (property) {
            case 'width':
                return isDesktop ? "80%" : "97%";
            case 'marginLeft':
                return isDesktop ? "1%" : "0%";
            default:
                break;
        }
    };

    const handleClearFilters = (e) => {
        e.stopPropagation();
        sessionStorage.removeItem(filterState.tableURL + "filters" + UserInfo.instanceId);
        return setFilterState({
            loading: true,
            filters: [],
            clearFilters: true,
        });
    };

    const renderLocationLabel = (locationName, numberOfAssets) => (
        filterState.loading ? (
            <>
                {`${locationName}`} <CircularLoading size={15} marginLeft={1} />
            </>
        ) : (
            <>
                {`${locationName} (${numberOfAssets})`}
            </>
        )
    );

    const NoData = () => (
        <Grid container component={Paper}>
            <Grid item xs={12} align='center'>
                <img
                    style={{ paddingTop: '4%' }}
                    src={NoTableDataIcon}
                    alt='No Data'
                />
            </Grid>
            <Grid item xs={12}>
                <Typography
                    variant='h5'
                    align='center'
                    style={{ marginTop: '2%', paddingBottom: '2%', color: '#1A173B' }}
                >
                    {filterState.loading ? "Resetting Results..." : "No results to display. Reset filters to see more results"}
                </Typography>
            </Grid>
            {filterState.loading && (
                <Grid item xs={12} sx={{ paddingBottom: '20px' }}>
                    <LoadingBar paddingTop={0} />
                </Grid>
            )}
        </Grid>
    );

    const key_values = [
        { title: 'Cylinder Type', value: state.selectedCylinder['cylinderType'] },
        { title: 'Material Type', value: state.selectedCylinder['materialTypeName'] },
        { title: 'Gas Capacity', value: renderWeight(state.selectedCylinder.maxGasCapacityLbs) },
        { title: 'Tag Number', value: state.selectedCylinder['tagNumber'] },
        { title: 'Acquisition Date', value: formatDate('acquisitionDate') },
        { title: 'Current Gas Weight', value: renderWeight(state.selectedCylinder.currentGasWeightLbs) },
    ];
    
    const pageTitle = determinePageTitle();
    const toolTipText = determineTootTipText();

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setState({ expanded: '', selectedCylinder: '', selectedCylinderLocationId: '' });
        setFilterState({ loading: true });
        const getFilteredData = async () => {
            const filteredData = await getRawLocationData();
            return filteredData;
        };

        getFilteredData()
            .then(filteredData => {
                const locations = filteredData.filter(l => l.cylinders.length > 0);
                setState({
                    dropDownLocations: locations,
                    savedLocations: locations,
                })
            })
            .finally(() => setFilterState({ loading: false }));
    }, [filterState.filters, filterState.clearFilters]);

    return (
        <>
            {isDesktop && (
                <BreadCrumbs
                    path={[
                        { text: 'Inventory', to: '/Inventory' },
                        { text: `${pageTitle}` }
                    ]}
                />
            )}
            <Grid
                container
                direction="row"
                alignItems="center"
                columns={10}
                sx={{ width: getStyle('width') }}
            >
                <Grid item>
                    <IconButton aria-label="close" onClick={handleXButton}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={6} md={4}>
                    <PageTitleWithToolTip
                        pageTitle={pageTitle}
                        toolTipText={isDesktop ? toolTipText : null}
                    />
                </Grid>
                {!isMobile && (
                    <Grid item sx={{ marginLeft: 'auto' }}>
                        {state.saving ? (
                            <LoadingButton
                                text="Saving..."
                                loadingWheelSize={24}
                                color={trakrefBlue}
                            />
                        ) : (
                            <PrimaryButton
                                buttonText={pageTitle}
                                onClick={moveCylinders}
                                disabled={state.submitButtonDisabled}
                            />
                        )}
                    </Grid>
                )}
            </Grid>

            {state.loading && (
                <LoadingBar />
            )}

            {!state.loading && (
                <>
                    <Grid
                        container
                        spacing={4}
                        columns={8}
                        direction="row"
                        sx={{
                            width: '70%',
                            marginTop: '.2%',
                            marginLeft: '1%',
                            display: { xs: 'none', md: isDesktop ? 'flex' : 'none' }
                        }}
                    >
                        <Grid item xs={4} md={4} lg={4}>
                            <Select
                                label="Send To *"
                                variant="filled"
                                value={state.movedTo}
                                options={state.dropdownOptions}
                                onChange={e => handleInput('movedTo', e.target.value)}
                                sx={{ backgroundColor: inputGrey }}
                                error={state.errors.movedTo}
                            />
                        </Grid>
                        <Grid item xs={4} md={4} lg={4}>
                            <DateInput
                                allowPast
                                maxDate={today}
                                label="Date Sent *"
                                date={state.dateMoved}
                                onChange={date => handleInput('dateMoved', date)}
                                error={state.errors.dateMoved}
                                customHelperText={state.errors.dateMovedErrorText}
                            />
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        spacing={2}
                        columns={8}
                        direction="row"
                        sx={{
                            width: getStyle('width'),
                            marginTop: '2%',
                            marginLeft: getStyle('marginLeft')
                        }}
                    >
                        <Grid item xs={8} md={2}>
                            <Typography
                                variant='subtitle1'
                                sx={selectCylinderStyle}
                            >
                                {!isDesktop && '1.'}Select Cylinders
                            </Typography>
                        </Grid>

                        {filterState.filterListOptions.length > 0 && (
                            <Grid item xs={8}>
                                <FilterList
                                    tableURL={filterState.tableURL}
                                    filters={filterState.filterListOptions}
                                    filterValues={filterState.filterValues}
                                    appliedFilters={filterState.filters}
                                    setAppliedFilters={appliedFilters => setFilterState({ filters: appliedFilters })}
                                    selectedFilter={filterState.selectedFilter}
                                    setSelectedFilter={selectedFilter => setFilterState({ selectedFilter })}
                                    handleClearFilters={handleClearFilters}
                                    setClearFilters={clearFilters => setFilterState({ clearFilters })}
                                    filterProps={{
                                        startDateMax: today,
                                        endDateMax: today,
                                    }}
                                />
                            </Grid>
                        )}
                        
                        <Grid item xs={8} mt={2} mb={2}>
                            <TextField
                                label="Search For Location"
                                variant="outlined"
                                value={state.locationSearchTerm}
                                onChange={e => handleLocationSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: isMobile ? '100%' : '50%',
                                    '& .MuiInputBase-input': { padding: '10px' },
                                }}
                            />
                        </Grid>

                        {state.dropDownLocations.length > 0 ? (
                            state.dropDownLocations.map((location) => (
                                <Grid item xs={12} md={8}>
                                    <Accordion
                                        variant='outlined'
                                        disabled={filterState.loading}
                                        expanded={state.expanded === location.id}
                                        onChange={(_, isExpanded) => handleOpen(location.id, isExpanded)}
                                        TransitionProps={{ unmountOnExit: true }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                        >
                                            <Typography
                                                sx={locationLabelStyle}
                                            >
                                                {renderLocationLabel(location.name, location.cylinders.length)}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TextField
                                                label="Search For Cylinders"
                                                variant="outlined"
                                                value={state.searchTerm}
                                                onChange={e => handleSearch(e.target.value, location.id)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={searchBarStyle}
                                            />
                                            <CylinderSelect
                                                locationId={location.id}
                                                setChecked={setChecked}
                                                setCylinderDetails={setCylinderDetails}
                                                cylinders={location.cylinders}
                                                cylinderIdsToMove={state.cylinderIdsToMove}
                                                selectedCylinderId={state.selectedCylinder.id}
                                                closeCylinderDetails={closeCylinderDetails}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                    <Collapse
                                        unmountOnExit
                                        in={
                                            state.selectedCylinder
                                            && state.selectedCylinderLocationId === location.id
                                        }
                                        timeout="auto"
                                    >
                                        <CylinderDetails
                                            key_values={key_values}
                                            closeCylinderDetails={closeCylinderDetails}
                                            openReweighModal={() => setReweighState({ open: true })}
                                        />
                                    </Collapse>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={8}>
                                <NoData />
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        
            <ReweighModal
                open={state.reweigh.open}
                header="Reweigh Cylinder"
                onSubmit={handleReweighSubmit}
                closeModal={() => setReweighState({ open: false })}
                state={state}
                setReweighState={setReweighState}
                reweighModalButtonDisabled={reweighModalButtonDisabled}
            />

            {isMobile && (
                <Grid 
                    container
                    spacing={2}
                    columns={8}
                    sx={{
                        width: getStyle('width'), 
                        marginTop: '2%', 
                        marginLeft: getStyle('marginLeft')
                    }}
                >
                    <Grid item xs={12} sx={{ marginTop: 2 }}>
                        <Divider />
                    </Grid>

                    <Grid item xs={12} sx={{ marginTop: 2 }}>
                        <Typography
                            variant='subtitle1'
                            sx={selectCylinderStyle}
                        >
                            2.Action Details
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Select
                            label="Send To *"
                            variant="outlined"
                            value={state.movedTo}
                            options={state.dropdownOptions}
                            onChange={e => handleInput('movedTo', e.target.value)}
                            sx={{ backgroundColor: inputGrey }}
                            error={state.errors.movedTo}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DateInput
                            allowPast
                            variant="outlined"
                            maxDate={today}
                            label="Date Sent *"
                            date={state.dateMoved}
                            onChange={date => handleInput('dateMoved', date)}
                            error={state.errors.dateMoved}
                            customHelperText={state.errors.dateMovedErrorText}
                        />
                    </Grid>
                    {isMobile && (
                        <Grid item xs={12} mb={"10%"}>
                            <PrimaryButton
                                fullWidth
                                useLargeButton
                                buttonText={pageTitle}
                                onClick={moveCylinders}
                                disabled={state.submitButtonDisabled}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </>
    );
};

export default MoveCylinders;
